/**
 * Type Schemas Tool — "Sinks Not Pipes"
 *
 * Exposes a human-readable summary of available @stackwright-pro/types schemas.
 * The foreman uses this for schema-based routing: "which otter owns this domain?"
 * instead of hardcoded capability descriptions in the prompt.
 */
import { McpServer } from '@modelcontextprotocol/sdk/server/mcp.js';
import { z } from 'zod';

/**
 * Schema summary returned by the tool and written to .stackwright/type-schemas.json
 */
export interface TypeSchemaSummary {
  version: '1.0';
  generatedAt: string;
  domains: {
    workflow: {
      description: string;
      schemas: string[];
      otter: string;
      artifactKey: string;
    };
    auth: {
      description: string;
      schemas: string[];
      otter: string;
      artifactKey: string;
    };
    openapi: {
      description: string;
      interfaces: string[];
      otter: string;
      artifactKey: string;
    };
    pulse: {
      description: string;
      interfaces: string[];
      note: string;
    };
    enterprise: {
      description: string;
      interfaces: string[];
      note: string;
    };
  };
}

export function buildTypeSchemaSummary(): TypeSchemaSummary {
  return {
    version: '1.0',
    generatedAt: new Date().toISOString(),
    domains: {
      workflow: {
        description: 'Workflow DSL — step definitions, auth blocks, field types, conditions',
        schemas: [
          'WorkflowFileSchema',
          'WorkflowDefinitionSchema',
          'WorkflowStepSchema',
          'WorkflowStepTypeSchema',
          'WorkflowFieldSchema',
          'WorkflowActionSchema',
          'WorkflowAuthSchema',
          'WorkflowThemeSchema',
          'TransitionConditionSchema',
          'PersistenceSchema',
        ],
        otter: 'stackwright-pro-form-wizard-otter',
        artifactKey: 'workflowConfig',
      },
      auth: {
        description: 'Authentication providers — PKI/CAC, OIDC, RBAC configuration',
        schemas: [
          'authConfigSchema',
          'pkiConfigSchema',
          'oidcConfigSchema',
          'rbacConfigSchema',
          'componentAuthSchema',
          'authUserSchema',
          'authSessionSchema',
        ],
        otter: 'stackwright-pro-auth-otter',
        artifactKey: 'authConfig',
      },
      openapi: {
        description: 'OpenAPI spec integration — collection config, endpoint filters, actions',
        interfaces: [
          'OpenAPIConfig',
          'ActionConfig',
          'EndpointFilter',
          'ApprovedSpec',
          'PrebuildSecurityConfig',
          'SiteConfig',
          'ValidationResult',
        ],
        otter: 'stackwright-pro-api-otter',
        artifactKey: 'apiConfig',
      },
      pulse: {
        description: 'Real-time data polling — source-agnostic polling options and states',
        interfaces: ['PulseOptions', 'PulseMeta', 'PulseState'],
        note: 'React-bound types (PulseProps, PulseIndicatorProps) remain in @stackwright-pro/pulse',
      },
      enterprise: {
        description: 'Enterprise collection access — multi-tenant provider extension',
        interfaces: [
          'EnterpriseCollectionProvider',
          'CollectionProvider',
          'CollectionEntry',
          'CollectionListOptions',
          'CollectionListResult',
          'TenantFilter',
          'Collection',
        ],
        note: 'CollectionProvider, CollectionEntry, CollectionListOptions, and CollectionListResult are re-exported from @stackwright/types (^1.5.0). EnterpriseCollectionProvider, TenantFilter, and Collection are Pro-only extensions.',
      },
    },
  };
}

export function registerTypeSchemasTool(server: McpServer): void {
  server.tool(
    'stackwright_pro_get_type_schemas',
    'Returns a structured summary of all canonical @stackwright-pro/types schemas, organized by domain. Use this to determine which otter owns a given schema and what artifact key to expect.',
    {
      format: z
        .enum(['full', 'domains-only'])
        .optional()
        .default('full')
        .describe('full = complete summary with all fields; domains-only = just domain names'),
    },
    async ({ format }) => {
      const summary = buildTypeSchemaSummary();
      const output = format === 'domains-only' ? Object.keys(summary.domains) : summary;
      return {
        content: [{ type: 'text', text: JSON.stringify(output, null, 2) }],
      };
    }
  );
}
